import { IPrintFileJob, IPrintFileLineItem } from '../../api/printFileJobs';

export interface IStatusStyle {
    label: string;
    color: string;
    textColor?: string;
}

const FALLBACK: IStatusStyle = { label: '—', color: '#898781' };

export const PRINT_FILE_STATUS: Record<string, IStatusStyle> = {
    created: { label: 'Created', color: '#898781' },
    pending_design_review: { label: 'Pending design review', color: '#fab219', textColor: '#1a1a1a' },
    in_review: { label: 'In review', color: '#3b82f6' },
    reviewed: { label: 'Reviewed', color: '#0ca30c' },
};

export const REVIEW_RESULT: Record<string, IStatusStyle> = {
    ok: { label: 'OK', color: '#0ca30c' },
    need_design: { label: 'Need design', color: '#ec835a' },
    need_cs: { label: 'Need CS', color: '#d03b3b' },
};

export const JOB_STATUS: Record<string, IStatusStyle> = {
    queued: { label: 'Queued', color: '#898781' },
    running: { label: 'Running', color: '#3b82f6' },
    succeeded: { label: 'Succeeded', color: '#0ca30c' },
    failed: { label: 'Failed', color: '#d03b3b' },
    skipped: { label: 'Skipped', color: '#fab219', textColor: '#1a1a1a' },
};

export const JOB_STATUS_ORDER = ['running', 'queued', 'failed', 'skipped', 'succeeded'];

export const styleFor = (map: Record<string, IStatusStyle>, value: string | null | undefined): IStatusStyle => {
    if (!value) return FALLBACK;
    return map[value] ?? { label: value, color: '#898781' };
};

export const fmtDateTime = (iso: string | null | undefined): string => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    });
};

/** Human readable elapsed time between two ISO timestamps. */
export const fmtDuration = (startIso: string | null | undefined, endIso: string | null | undefined): string => {
    if (!startIso || !endIso) return '—';
    const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
    if (Number.isNaN(ms) || ms < 0) return '—';
    if (ms < 1000) return `${ms} ms`;
    const sec = ms / 1000;
    if (sec < 60) return `${sec.toFixed(1)} s`;
    const min = Math.floor(sec / 60);
    const rest = Math.round(sec - min * 60);
    if (min < 60) return `${min}m ${String(rest).padStart(2, '0')}s`;
    const h = Math.floor(min / 60);
    return `${h}h ${String(min - h * 60).padStart(2, '0')}m`;
};

export const fmtBool = (v: boolean | null | undefined): string => (v === null || v === undefined ? '—' : v ? 'Yes' : 'No');

/** File name and extension taken from a URL path, for display next to a preview. */
export const urlFileInfo = (url: string | null | undefined): { name: string; ext: string } => {
    if (!url) return { name: '—', ext: '' };
    try {
        const path = new URL(url).pathname;
        const name = decodeURIComponent(path.split('/').pop() || '');
        const dot = name.lastIndexOf('.');
        return { name: name || url, ext: dot >= 0 ? name.slice(dot + 1).toLowerCase() : '' };
    } catch (e) {
        return { name: url, ext: '' };
    }
};

/** Counts of jobs by status, used for the per-row summary and the page summary. */
export const countJobsByStatus = (jobs: IPrintFileJob[]): Record<string, number> =>
    jobs.reduce<Record<string, number>>((acc, j) => {
        const key = j.status || 'unknown';
        acc[key] = (acc[key] || 0) + 1;
        return acc;
    }, {});

export interface ILightboxImage {
    title: string;
    url: string;
    subtitle?: string;
}

/** Every non-null image URL of a line item, in a stable display order. */
export const collectLineItemImages = (item: IPrintFileLineItem): ILightboxImage[] => {
    const out: ILightboxImage[] = [];
    const push = (title: string, url: string | null | undefined, subtitle?: string) => {
        if (url) out.push({ title, url, subtitle });
    };
    push('Original (Shopify)', item.originalUrl, `line item ${item.lineItemId}`);
    push('Mockup (Shopify)', item.mockupUrl, `line item ${item.lineItemId}`);
    push('Print file (resolved)', item.printFileUrl, `line item ${item.lineItemId}`);
    push('Draft print-ready', item.printFile?.draftPrintReadyUrl, `print file ${item.printFile?.id}`);
    push('Final print-ready', item.printFile?.finalPrintReadyUrl, `print file ${item.printFile?.id}`);
    (item.jobs || []).forEach((job, i) => {
        const tag = `job ${job.id}${job.current ? ' (current)' : ''} · #${item.jobs.length - i}`;
        push('Background removed', job.backgroundRemovedUrl, tag);
        push('Preprocessed print-ready', job.preprocessedPrintReadyUrl, tag);
    });
    return out;
};

/** Checkerboard background so transparent PNG areas are visible. */
export const checkerboardSx = (mode: 'light' | 'dark') => {
    const a = mode === 'dark' ? '#3a3a3a' : '#d9d9d9';
    const b = mode === 'dark' ? '#262626' : '#ffffff';
    return {
        backgroundColor: b,
        backgroundImage: `linear-gradient(45deg, ${a} 25%, transparent 25%), linear-gradient(-45deg, ${a} 25%, transparent 25%), linear-gradient(45deg, transparent 75%, ${a} 75%), linear-gradient(-45deg, transparent 75%, ${a} 75%)`,
        backgroundSize: '16px 16px',
        backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0',
    };
};
